import { observer } from 'mobx-react-lite';
import { ElementType, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { To, useNavigate, useParams } from 'react-router';
import { StepElement } from '../../../lib/components/compound/progress/stepper';
import { useStepper } from '../partials/actionStepperPartial';
import { Description } from './steps/description';
import { BenefitSelection } from './steps/benefitSelection';
import { Summary } from './steps/summary';
import { Conclusion } from './steps/conclusion';
import { useStore } from '../../../hooks/useStore';
import { useCreateBenefitSet } from './hooks/useCreateBenefitSet';
import { useSeedCardOffer } from './hooks/useSeedCardOffer';
import { EmployeeAssignment } from './steps/employeeAssignment';
import { EmployeeAssignmentSummary } from './steps/employeeAssignmentSummary';
import { useActions } from '../../../hooks/useActions';
import { EmployeeSelectionBenefitSetTable } from '../../../lib/components/tables/employee/employeeSelectionBenefitSet';

export interface StepDef {
  content: FC;
  title?: string | null;
  sectionTitle?: string | null;
  stepperConfig?: Array<Omit<StepElement, 'icon' & { icon?: ElementType }>>;
  nextText?: string;
  hideNavigation?: boolean;
}

type BenefitSetProcess =
  | 'create_benefit_set'
  | 'edit_benefit_set'
  | 'assign_benefit_set'
  | 'assign_benefit_set_to_employees';

export const CreateBenefitSet = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { newBenefitSet } = useCreateBenefitSet();
  const params = useParams();
  const navigate = useNavigate();
  useSeedCardOffer((params.type as BenefitSetProcess) === 'edit_benefit_set');

  const description: StepDef = {
    content: Description,
    sectionTitle: t('benefitSet:descriptionTitle')
  };

  const employeeSelection: StepDef = {
    content: EmployeeSelectionBenefitSetTable,
    sectionTitle: t('benefitSet:chooseEmployeesForSet', { name: newBenefitSet.name_de })
  };

  const employeeAssignment: StepDef = {
    content: EmployeeAssignment,
    sectionTitle:
      (params.type as BenefitSetProcess) === 'assign_benefit_set_to_employees'
        ? t('benefitSet:chooseMonth')
        : t('benefitSet:assignBenefitSet')
  };

  const benefitSelection: StepDef = {
    content: BenefitSelection,
    sectionTitle: t('benefitSet:selectionTitle', { name: newBenefitSet.name_de })
  };

  const summary: StepDef = {
    content: Summary,
    sectionTitle: t('benefitSet:summaryTitle', { name: newBenefitSet.name_de })
  };

  const employeeAssignmentSummary: StepDef = {
    content: EmployeeAssignmentSummary,
    sectionTitle: t('benefitSet:summary')
  };

  const conclusion: StepDef = {
    content: Conclusion,
    sectionTitle: null
  };

  const createBenefitSetStepper = [
    {
      title: t('benefitSet:description')
    },
    {
      title: t('benefitSet:selection')
    },
    {
      title: t('benefitSet:summary')
    }
  ];

  const assignBenefitSetStepper = [
    {
      title: t('benefitSet:choose')
    },
    {
      title: t('benefitSet:summary')
    }
  ];

  const assignBenefitSetToEmployeesStepper = [
    {
      title: t('benefitSet:chooseEmployees')
    },
    {
      title: t('benefitSet:chooseMonth')
    },
    {
      title: t('benefitSet:summary')
    }
  ];

  const step: Record<BenefitSetProcess, Array<StepDef>> = {
    create_benefit_set: [
      {
        ...description,
        title: t('benefitSet:create'),
        stepperConfig: createBenefitSetStepper
      },
      benefitSelection,
      summary,
      conclusion
    ],
    edit_benefit_set: [
      {
        ...description,
        title: t('benefitSet:edit'),
        stepperConfig: createBenefitSetStepper
      },
      benefitSelection,
      summary,
      conclusion
    ],
    assign_benefit_set: [
      {
        ...employeeAssignment,
        title: t('benefitSet:assignBenefitSet'),
        stepperConfig: assignBenefitSetStepper
      },
      employeeAssignmentSummary,
      conclusion
    ],
    assign_benefit_set_to_employees: [
      {
        ...employeeSelection,
        title: t('benefitSet:assignBenefitSet'),
        stepperConfig: assignBenefitSetToEmployeesStepper
      },
      employeeAssignment,
      employeeAssignmentSummary,
      conclusion
    ]
  };

  const {
    setTitle,
    setSectionTitle,
    setStepperConfig,
    setBackToPath,
    setBackToTitle,
    setShowHelpButton,
    setCustomCancelHandler,
    setHasChangedFields
  } = useStepper();
  const { setSelectedEmployees, selectedEmployees } = useActions();

  const defaultStep = { ...step[params.type as BenefitSetProcess][1], title: 'Custom Benefit' };
  const [currentStep, setCurrentStep] = useState<StepDef>(defaultStep);

  const determineBackToPath = (path: To): string | To => {
    if (newBenefitSet.assignmentContext === 'employeeDashboard') {
      return `/employees/${selectedEmployees}/dashboard`;
    }

    if ((params.type as BenefitSetProcess) === 'assign_benefit_set') {
      return '/employees';
    }

    return path;
  };

  useEffect(() => {
    const handleCancel = (path: To) => {
      setSelectedEmployees({});
      navigate(determineBackToPath(path));
      newBenefitSet.clear();
    };

    setCustomCancelHandler(() => handleCancel);

    return () => setCustomCancelHandler(null);
  }, [params.type, newBenefitSet, navigate]);

  useEffect(() => {
    if (newBenefitSet.changedFields.length > 0) {
      setHasChangedFields(true);
    } else {
      setHasChangedFields(false);
    }
  }, [newBenefitSet.changedFields.length]);

  useEffect(() => {
    companyStore.fetchBookedBenefits();
    setStepperConfig(createBenefitSetStepper);
    setBackToTitle(t('custom:back_to_overview'));
    setBackToPath(determineBackToPath('/benefits/benefit-set'));
  }, []);

  useEffect(() => {
    setShowHelpButton(
      [
        t('benefitSet:descriptionTitle'),
        t('benefitSet:selectionTitle', { name: newBenefitSet.name_de }),
        t('benefitSet:assignBenefitSet'),
        t('benefitSet:chooseEmployeesForSet', { name: newBenefitSet.name_de }),
        t('benefitSet:chooseMonth')
      ].includes(currentStep.sectionTitle ?? '')
    );
  }, [currentStep]);

  useEffect(() => {
    if (params.type && !step[params.type as BenefitSetProcess]) {
      navigate('/404');
    } else {
      const nextStep =
        params.type && params.step
          ? step[params.type as BenefitSetProcess][+params.step - 1]
          : currentStep;
      setCurrentStep(nextStep);
      nextStep.title !== undefined && setTitle(nextStep.title ?? undefined);
      nextStep.sectionTitle !== undefined && setSectionTitle(nextStep.sectionTitle ?? undefined);
      nextStep.stepperConfig && setStepperConfig(nextStep.stepperConfig);
    }
  }, [params]);

  useEffect(() => {
    const fetchData = async () => {
      await companyStore.fetchBookedBenefits();
    };
    fetchData();
  }, [companyStore.company?.id]);

  const Step = currentStep.content;

  return <Step />;
});
